import { z } from 'zod';
import { CapitalActionNoticeStatus } from 'further-types/lp/capital-action-notice';
import { isMongoId, isValidPercentageValue } from '../../helpers';
import { CalendarDay } from '../../../utils';

export const UpdateCapitalActionNotice = z.object({
  id: z.string().refine(isMongoId()),
  capitalActionId: z.string().refine(isMongoId()),
  capitalActionName: z.string().min(1, 'Capital action name is required.'),
  capitalActionDueDate: z.instanceof(CalendarDay).optional(),
  lcyRecipientBankAccountId: z.string().refine(isMongoId()).optional(),
  status: z.nativeEnum(CapitalActionNoticeStatus),
  noticeDate: z.instanceof(CalendarDay).optional(),
  mainContactEmails: z.string().optional(),
  noticeNarrative: z.string().optional(),
  generalCapitalBreakdown: z
    .object({
      investmentsPercentage: isValidPercentageValue.optional(),
      investmentsAmount: z
        .number()
        .min(0, 'Enter a positive value.')
        .optional(),
      managementFeesPercentage: isValidPercentageValue.optional(),
      managementFeesAmount: z
        .number()
        .min(0, 'Enter a positive value.')
        .optional(),
      otherExpensesPercentage: isValidPercentageValue.optional(),
      otherExpensesAmount: z
        .number()
        .min(0, 'Negative values are invalid.')
        .optional(),
    })
    .optional(),
  notificationPreferences: z
    .object({
      notifyViaEmail: z.boolean().optional(),
      attachPdfNoticeToEmail: z.boolean().optional(),
      sendReminderEmails: z.boolean().optional(),
      reminderEmailFrequencyInDays: z
        .number()
        .min(1, 'Enter a value greater than 0.')
        .optional(),
    })
    .optional(),
  perInvestorNotificationSettings: z.array(
    z.object({
      investorId: z.string().refine(isMongoId()),
      notifyViaEmail: z.boolean().optional(),
      sendReminderEmails: z.boolean().optional(),
    }),
  ),
  resendPublishedNotices: z.boolean(),
});

export const PublishCapitalActionNotice = z.object({
  id: z.string().refine(isMongoId()),
  capitalActionId: z.string().refine(isMongoId()),
  capitalActionName: z.string().min(1, 'Enter a capital action name.'),
  capitalActionDueDate: z.instanceof(CalendarDay, {
    message: 'Enter a capital action due date.',
  }),
  lcyRecipientBankAccountId: z.string().refine(isMongoId()),
  status: z.nativeEnum(CapitalActionNoticeStatus),
  noticeDate: z.instanceof(CalendarDay, {
    message: 'Notice date is required.',
  }),
  mainContactEmails: z.string().min(1, 'Enter main contact emails.'),
  noticeNarrative: z.string().min(1, 'Enter notice narrative.'),
  generalCapitalBreakdown: z
    .object({
      investmentsPercentage: isValidPercentageValue.optional(),
      investmentsAmount: z
        .number()
        .min(0, 'Enter a positive value.')
        .optional(),
      managementFeesPercentage: isValidPercentageValue.optional(),
      managementFeesAmount: z
        .number()
        .min(0, 'Enter a positive value.')
        .optional(),
      otherExpensesPercentage: isValidPercentageValue.optional(),
      otherExpensesAmount: z
        .number()
        .min(0, 'Enter a positive value.')
        .optional(),
    })
    .optional(),
  notificationPreferences: z
    .object({
      notifyViaEmail: z.boolean(),
      attachPdfNoticeToEmail: z.boolean(),
      sendReminderEmails: z.boolean(),
      reminderEmailFrequencyInDays: z
        .number()
        .min(1, 'Enter a value greater than 0.')
        .optional(),
    })
    .optional(),
  perInvestorNotificationSettings: z.array(
    z.object({
      investorId: z.string().refine(isMongoId()),
      notifyViaEmail: z.boolean().optional(),
      sendReminderEmails: z.boolean().optional(),
    }),
  ),
  resendPublishedNotices: z.boolean(),
});
